// BroadcastScreen.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './BroadcastScreen.css';

const API_BASE_URL = 'https://mhsdev.online/MHS-WEB/api';
const USER_MOBILE_KEY = 'userMobile';

// Splits a message into plain-text fragments and {{tag}} fragments so
// merge tags can be highlighted in the live preview.
const splitTags = (text) => {
  if (!text) return [];
  return text.split(/(\{\{[^}]+\}\})/g).filter((part) => part !== '');
};

const initials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();

const currentTime = () =>
  new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

export default function BroadcastScreen() {
  const navigate = useNavigate();
  const [currentUserMobile, setCurrentUserMobile] = useState(null);
  const [isLoadingMobile, setIsLoadingMobile] = useState(true);
  const [step, setStep] = useState(1);

  // Step 1: Compose
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatePickerVisible, setTemplatePickerVisible] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [messageBody, setMessageBody] = useState('');
  const [imageUris, setImageUris] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);

  const [saveFormVisible, setSaveFormVisible] = useState(false);
  const [saveTitle, setSaveTitle] = useState('');
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);

  // Step 2: Recipients
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [search, setSearch] = useState('');
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState(new Set());
  const [selectedStudentRoster, setSelectedStudentRoster] = useState(new Map());

  const searchDebounceRef = useRef(null);
  const [isSending, setIsSending] = useState(false);

  // Load user mobile
  useEffect(() => {
    const mobile = localStorage.getItem(USER_MOBILE_KEY);
    if (mobile) {
      setCurrentUserMobile(mobile);
    } else {
      alert('No mobile number found. Please log in again.');
      navigate('/auth/phone');
    }
    setIsLoadingMobile(false);
  }, [navigate]);

  // Fetch templates on mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/broadcast_api.php`, {
        params: { action: 'list_templates' },
        timeout: 10000,
      });
      setTemplates(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const applyTemplate = (template) => {
    setSelectedTemplate(template);
    setMessageBody(template.message_body);
    setTemplatePickerVisible(false);
  };

  const startBlank = () => {
    setSelectedTemplate(null);
    setMessageBody('');
    setTemplatePickerVisible(false);
  };

  const insertTag = (tag) => {
    setMessageBody((current) => (current ? `${current} ${tag}` : tag));
  };

  const handlePickImages = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + imageFiles.length > 5) {
      alert('Maximum 5 images allowed.');
      return;
    }
    const newUris = files.map((file) => URL.createObjectURL(file));
    setImageUris((prev) => [...prev, ...newUris]);
    setImageFiles((prev) => [...prev, ...files]);
  };

  const removeImage = (index) => {
    setImageUris((prev) => {
      const newUris = [...prev];
      URL.revokeObjectURL(newUris[index]);
      newUris.splice(index, 1);
      return newUris;
    });
    setImageFiles((prev) => {
      const newFiles = [...prev];
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const openSaveForm = (template) => {
    setSaveTitle(template?.event_title || '');
    setSelectedTemplate(template || null);
    setSaveFormVisible(true);
  };

  const handleSaveTemplate = async (updateExisting) => {
    if (messageBody.trim().length === 0) {
      alert('Write a message before saving it as a template.');
      return;
    }
    if (saveTitle.trim().length === 0) {
      alert('Give this template a short title.');
      return;
    }

    setIsSavingTemplate(true);
    try {
      const payload = {
        event_title: saveTitle.trim(),
        message_body: messageBody,
      };
      if (updateExisting && selectedTemplate) {
        payload.id = selectedTemplate.id;
        payload.event_type = selectedTemplate.event_type;
        payload.available_tags = selectedTemplate.available_tags;
        payload.description = selectedTemplate.description;
      }

      const response = await axios.post(
        `${API_BASE_URL}/broadcast_api.php?action=save_template`,
        payload,
        { timeout: 10000 }
      );

      if (response.data?.success) {
        alert('Template saved successfully.');
        setSaveFormVisible(false);
        fetchTemplates();
      } else {
        alert(response.data?.error || 'Failed to save template.');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Could not save the template. Please check your connection.');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const goToRecipients = () => {
    if (messageBody.trim().length === 0 && imageFiles.length === 0) {
      alert('Write a message or attach images first.');
      return;
    }
    setStep(2);
  };

  useEffect(() => {
    if (step !== 2) return;
    fetchClasses();
    fetchStudents();
  }, [step]);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/broadcast_api.php`, {
        params: { action: 'list_classes' },
        timeout: 10000,
      });
      setClasses(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchSections = async (cls) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/broadcast_api.php`, {
        params: { action: 'list_sections', class: cls || '' },
        timeout: 10000,
      });
      setSections(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  const fetchStudents = async () => {
    setStudentsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/broadcast_api.php`, {
        params: {
          action: 'list_students',
          class: selectedClass || '',
          section: selectedSection || '',
          search: search || '',
        },
        timeout: 10000,
      });
      const list = Array.isArray(response.data) ? response.data : [];
      setStudents(list);
      // Keep a roster of full student objects for everything the user has
      // selected so far, even after filters change the visible list.
      setSelectedStudentRoster((prevRoster) => {
        const nextRoster = new Map(prevRoster);
        list.forEach((s) => {
          if (selectedStudentIds.has(s.id)) nextRoster.set(s.id, s);
        });
        return nextRoster;
      });
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setStudentsLoading(false);
    }
  };

  const handleSelectClass = (cls) => {
    setSelectedClass(cls);
    setSelectedSection(null);
    fetchSections(cls);
  };

  useEffect(() => {
    if (step !== 2) return;
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass, selectedSection]);

  useEffect(() => {
    if (step !== 2) return;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchStudents();
    }, 400);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const toggleStudent = (student) => {
    setSelectedStudentIds((current) => {
      const next = new Set(current);
      if (next.has(student.id)) next.delete(student.id);
      else next.add(student.id);
      return next;
    });
    setSelectedStudentRoster((current) => {
      const next = new Map(current);
      if (next.has(student.id)) next.delete(student.id);
      else next.set(student.id, student);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedStudentIds((current) => {
      const next = new Set(current);
      students.forEach((s) => next.add(s.id));
      return next;
    });
    setSelectedStudentRoster((current) => {
      const next = new Map(current);
      students.forEach((s) => next.set(s.id, s));
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedStudentIds(new Set());
    setSelectedStudentRoster(new Map());
  };

  const handleSend = async () => {
    if (!currentUserMobile) {
      alert('User mobile not loaded.');
      return;
    }
    if (selectedStudentIds.size === 0) {
      alert('Select at least one student to send to.');
      return;
    }

    setIsSending(true);
    try {
      const formData = new FormData();
      formData.append('sender_mobile', currentUserMobile);
      formData.append('message_body', messageBody);
      formData.append('student_ids', Array.from(selectedStudentIds).join(','));

      imageFiles.forEach((file) => {
        formData.append('media[]', file);
      });

      const response = await axios.post(
        `${API_BASE_URL}/broadcast_api.php?action=send_broadcast`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 30000,
        }
      );

      if (response.data?.success) {
        alert(response.data.message || 'Messages sent.');
        navigate(-1);
      } else {
        alert(response.data?.error || 'Failed to send broadcast.');
      }
    } catch (error) {
      console.error('Error sending broadcast:', error);
      alert('Could not send the broadcast. Please check your connection.');
    } finally {
      setIsSending(false);
    }
  };

  const previewFragments = useMemo(() => splitTags(messageBody), [messageBody]);
  const previewRoster = useMemo(
    () => Array.from(selectedStudentRoster.values()),
    [selectedStudentRoster]
  );

  if (isLoadingMobile) {
    return (
      <div className="brd-loading-screen">
        <div className="brd-spinner"></div>
        <p>Loading account…</p>
      </div>
    );
  }

  return (
    <div className="brd-shell">
      {/* ----- Top bar ----- */}
      <header className="brd-topbar">
        <button
          className="brd-icon-btn"
          onClick={() => (step === 2 ? setStep(1) : navigate(-1))}
          aria-label={step === 2 ? 'Back to compose' : 'Close'}
        >
          <i className={`fa ${step === 2 ? 'fa-arrow-left' : 'fa-times'}`}></i>
        </button>

        <div className="brd-topbar-center">
          <h1 className="brd-title">New Broadcast</h1>
          <ol className="brd-stepper" aria-label="Progress">
            <li className={`brd-step ${step >= 1 ? 'is-active' : ''}`}>
              <span className="brd-step-dot">1</span>
              <span className="brd-step-label">Compose</span>
            </li>
            <li className="brd-step-track">
              <span
                className="brd-step-track-fill"
                style={{ width: step === 2 ? '100%' : '0%' }}
              ></span>
            </li>
            <li className={`brd-step ${step === 2 ? 'is-active' : ''}`}>
              <span className="brd-step-dot">2</span>
              <span className="brd-step-label">Recipients</span>
            </li>
          </ol>
        </div>

        <div className="brd-icon-btn brd-icon-btn--ghost" aria-hidden="true">
          <i className="fa fa-bullhorn"></i>
        </div>
      </header>

      {/* ----- Main layout ----- */}
      <main className="brd-main">
        <div className="brd-layout">
          {/* ===== Primary panel ===== */}
          <section className="brd-panel">
            {step === 1 ? (
              <div className="brd-panel-body">
                <div className="brd-field">
                  <span className="brd-eyebrow">Template</span>
                  <button
                    className="brd-select-field"
                    onClick={() => setTemplatePickerVisible(true)}
                  >
                    <i className="fa fa-file-text-o"></i>
                    <span className="brd-select-field-text">
                      {selectedTemplate
                        ? selectedTemplate.event_title
                        : 'Choose a template or start blank'}
                    </span>
                    <i className="fa fa-chevron-down brd-select-field-chevron"></i>
                  </button>
                </div>

                <div className="brd-field">
                  <span className="brd-eyebrow">Message</span>
                  <textarea
                    className="brd-textarea"
                    value={messageBody}
                    onChange={(e) => setMessageBody(e.target.value)}
                    placeholder="Write your message here…"
                    rows={8}
                  />
                </div>

                {selectedTemplate?.available_tags && (
                  <div className="brd-field">
                    <div className="brd-tags-row">
                      {selectedTemplate.available_tags.split(',').map((tag) => {
                        const trimmedTag = tag.trim();
                        if (!trimmedTag) return null;
                        return (
                          <button
                            key={trimmedTag}
                            className="brd-tag-chip"
                            onClick={() => insertTag(trimmedTag)}
                          >
                            {trimmedTag}
                          </button>
                        );
                      })}
                    </div>
                    <p className="brd-hint">
                      Merge tags like <code>{'{{student_name}}'}</code> fill in
                      automatically per student when sent. Amount-based tags need to
                      be filled in manually before sending.
                    </p>
                  </div>
                )}

                <button
                  className="brd-text-link"
                  onClick={() => openSaveForm(selectedTemplate)}
                >
                  <i className="fa fa-save"></i> Save as template
                </button>

                <div className="brd-field brd-field--attachments">
                  <span className="brd-eyebrow">Attachments — up to 5</span>

                  {imageUris.length > 0 && (
                    <div className="brd-attachment-grid">
                      {imageUris.map((uri, index) => (
                        <div key={uri} className="brd-attachment-tile">
                          <img src={uri} alt="attachment" />
                          <button
                            className="brd-attachment-remove"
                            onClick={() => removeImage(index)}
                            aria-label="Remove image"
                          >
                            <i className="fa fa-times"></i>
                          </button>
                        </div>
                      ))}
                      {imageFiles.length < 5 && (
                        <label htmlFor="image-upload" className="brd-attachment-add">
                          <i className="fa fa-plus"></i>
                        </label>
                      )}
                    </div>
                  )}

                  {imageUris.length === 0 && (
                    <label htmlFor="image-upload" className="brd-dropzone">
                      <i className="fa fa-image"></i>
                      <span>Attach images</span>
                      <span className="brd-dropzone-sub">PNG or JPG, up to 5 files</span>
                    </label>
                  )}

                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    id="image-upload"
                    style={{ display: 'none' }}
                    onChange={handlePickImages}
                  />
                  <p className="brd-hint">
                    Images are sent along with the message but are not saved with
                    the template.
                  </p>
                </div>

                <div className="brd-panel-footer">
                  <button className="brd-btn brd-btn--primary" onClick={goToRecipients}>
                    Next: Select recipients <i className="fa fa-arrow-right"></i>
                  </button>
                </div>
              </div>
            ) : (
              <div className="brd-panel-body brd-panel-body--recipients">
                <div className="brd-field">
                  <span className="brd-eyebrow">Search</span>
                  <input
                    className="brd-search-field"
                    type="text"
                    placeholder="Search by name, father's name, or student ID"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                <div className="brd-field">
                  <span className="brd-eyebrow">Class</span>
                  <div className="brd-chip-row">
                    <button
                      className={`brd-filter-chip ${selectedClass === null ? 'is-active' : ''}`}
                      onClick={() => handleSelectClass(null)}
                    >
                      All classes
                    </button>
                    {classes.map((cls) => (
                      <button
                        key={cls}
                        className={`brd-filter-chip ${selectedClass === cls ? 'is-active' : ''}`}
                        onClick={() => handleSelectClass(cls)}
                      >
                        {cls}
                      </button>
                    ))}
                  </div>
                </div>

                {sections.length > 0 && (
                  <div className="brd-field">
                    <span className="brd-eyebrow">Section</span>
                    <div className="brd-chip-row">
                      <button
                        className={`brd-filter-chip brd-filter-chip--sm ${
                          selectedSection === null ? 'is-active' : ''
                        }`}
                        onClick={() => setSelectedSection(null)}
                      >
                        All sections
                      </button>
                      {sections.map((sec) => (
                        <button
                          key={sec}
                          className={`brd-filter-chip brd-filter-chip--sm ${
                            selectedSection === sec ? 'is-active' : ''
                          }`}
                          onClick={() => setSelectedSection(sec)}
                        >
                          {sec}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="brd-selection-bar">
                  <span className="brd-selection-count">
                    <strong>{selectedStudentIds.size}</strong> selected
                  </span>
                  <div className="brd-selection-actions">
                    <button onClick={selectAllVisible}>Select all shown</button>
                    <button onClick={clearSelection}>Clear</button>
                  </div>
                </div>

                <div className="brd-roster">
                  {studentsLoading ? (
                    <div className="brd-roster-loading">
                      <div className="brd-spinner"></div>
                    </div>
                  ) : students.length === 0 ? (
                    <p className="brd-roster-empty">No students match these filters.</p>
                  ) : (
                    <div className="brd-roster-list" role="listbox" aria-multiselectable="true">
                      {students.map((student) => {
                        const isSelected = selectedStudentIds.has(student.id);
                        return (
                          <div
                            key={student.id}
                            className={`brd-roster-row ${isSelected ? 'is-selected' : ''}`}
                            onClick={() => toggleStudent(student)}
                            role="option"
                            aria-selected={isSelected}
                          >
                            <span className={`brd-checkbox ${isSelected ? 'is-checked' : ''}`}>
                              {isSelected && <i className="fa fa-check"></i>}
                            </span>
                            <span className="brd-roster-avatar">{initials(student.name)}</span>
                            <span className="brd-roster-info">
                              <span className="brd-roster-name">{student.name}</span>
                              <span className="brd-roster-meta">
                                {student.class} · {student.section}{' '}
                                <span className="brd-roster-id">{student.student_id}</span>
                              </span>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="brd-panel-footer">
                  <button
                    className="brd-btn brd-btn--primary"
                    onClick={handleSend}
                    disabled={selectedStudentIds.size === 0 || isSending}
                  >
                    {isSending ? (
                      <span className="brd-spinner brd-spinner--sm"></span>
                    ) : (
                      <>
                        <i className="fa fa-send"></i>
                        Send to {selectedStudentIds.size}{' '}
                        {selectedStudentIds.size === 1 ? 'recipient' : 'recipients'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* ===== Live preview panel ===== */}
          <aside className="brd-preview">
            <span className="brd-eyebrow brd-eyebrow--preview">Live preview</span>

            <div className="brd-phone">
              <div className="brd-phone-bar">
                <span className="brd-phone-bar-avatar">
                  <i className="fa fa-graduation-cap"></i>
                </span>
                <span className="brd-phone-bar-name">School Notices</span>
              </div>
              <div className="brd-phone-screen">
                {messageBody.trim().length === 0 && imageUris.length === 0 ? (
                  <p className="brd-phone-placeholder">
                    Your message will appear here as you type.
                  </p>
                ) : (
                  <div className="brd-bubble">
                    {imageUris.length > 0 && (
                      <div className="brd-bubble-media">
                        {imageUris.slice(0, 4).map((uri, i) => (
                          <img key={uri} src={uri} alt="" />
                        ))}
                        {imageUris.length > 4 && (
                          <span className="brd-bubble-media-more">
                            +{imageUris.length - 4}
                          </span>
                        )}
                      </div>
                    )}
                    {messageBody.trim().length > 0 && (
                      <p className="brd-bubble-text">
                        {previewFragments.map((fragment, i) =>
                          /^\{\{[^}]+\}\}$/.test(fragment) ? (
                            <span key={i} className="brd-bubble-tag">
                              {fragment}
                            </span>
                          ) : (
                            <React.Fragment key={i}>{fragment}</React.Fragment>
                          )
                        )}
                      </p>
                    )}
                    <span className="brd-bubble-time">{currentTime()}</span>
                  </div>
                )}
              </div>
            </div>

            {step === 2 && (
              <div className="brd-recipient-summary">
                <div className="brd-recipient-stamp">
                  <span className="brd-recipient-stamp-count">
                    {selectedStudentIds.size}
                  </span>
                  <span className="brd-recipient-stamp-label">Selected</span>
                </div>
                {previewRoster.length > 0 && (
                  <div className="brd-recipient-avatars">
                    {previewRoster.slice(0, 6).map((s) => (
                      <span key={s.id} className="brd-recipient-avatar" title={s.name}>
                        {initials(s.name)}
                      </span>
                    ))}
                    {previewRoster.length > 6 && (
                      <span className="brd-recipient-avatar brd-recipient-avatar--more">
                        +{previewRoster.length - 6}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </aside>
        </div>
      </main>

      {/* ----- Template Picker Modal ----- */}
      {templatePickerVisible && (
        <div
          className="brd-modal-overlay"
          onClick={() => setTemplatePickerVisible(false)}
        >
          <div className="brd-modal" onClick={(e) => e.stopPropagation()}>
            <div className="brd-modal-header">
              <h3 className="brd-modal-title">Message templates</h3>
              <button
                className="brd-modal-close"
                onClick={() => setTemplatePickerVisible(false)}
                aria-label="Close"
              >
                <i className="fa fa-times"></i>
              </button>
            </div>

            <div className="brd-modal-body">
              <button className="brd-template-option brd-template-option--blank" onClick={startBlank}>
                <span className="brd-template-icon">
                  <i className="fa fa-edit"></i>
                </span>
                <span>
                  <span className="brd-template-title">Start from blank</span>
                  <span className="brd-template-desc">Write a fully custom message</span>
                </span>
              </button>

              {templatesLoading ? (
                <div className="brd-spinner" style={{ margin: '24px auto' }}></div>
              ) : (
                <div className="brd-template-list">
                  {templates.map((tpl) => (
                    <button
                      key={tpl.id}
                      className="brd-template-option"
                      onClick={() => applyTemplate(tpl)}
                    >
                      <span className="brd-template-icon">
                        <i className="fa fa-file-text-o"></i>
                      </span>
                      <span>
                        <span className="brd-template-title">{tpl.event_title}</span>
                        <span className="brd-template-desc">
                          {tpl.description ||
                            tpl.message_body.substring(0, 60) +
                              (tpl.message_body.length > 60 ? '…' : '')}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="brd-modal-footer">
              <button
                className="brd-btn brd-btn--outline"
                onClick={() => {
                  setTemplatePickerVisible(false);
                  openSaveForm();
                }}
              >
                <i className="fa fa-plus"></i> Create new template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ----- Save Template Modal ----- */}
      {saveFormVisible && (
        <div className="brd-modal-overlay" onClick={() => setSaveFormVisible(false)}>
          <div className="brd-modal brd-modal--sm" onClick={(e) => e.stopPropagation()}>
            <div className="brd-modal-header">
              <h3 className="brd-modal-title">Save template</h3>
              <button
                className="brd-modal-close"
                onClick={() => setSaveFormVisible(false)}
                aria-label="Close"
              >
                <i className="fa fa-times"></i>
              </button>
            </div>

            <div className="brd-modal-body">
              <div className="brd-field">
                <span className="brd-eyebrow">Title</span>
                <input
                  className="brd-search-field"
                  type="text"
                  value={saveTitle}
                  onChange={(e) => setSaveTitle(e.target.value)}
                  placeholder="e.g. Fee Reminder – Custom"
                />
              </div>
            </div>

            <div className="brd-modal-footer brd-modal-footer--stack">
              {selectedTemplate ? (
                <>
                  <button
                    className="brd-btn brd-btn--primary"
                    onClick={() => handleSaveTemplate(true)}
                    disabled={isSavingTemplate}
                  >
                    {isSavingTemplate ? (
                      <span className="brd-spinner brd-spinner--sm"></span>
                    ) : (
                      `Update "${selectedTemplate.event_title}"`
                    )}
                  </button>
                  <button
                    className="brd-btn brd-btn--outline"
                    onClick={() => handleSaveTemplate(false)}
                    disabled={isSavingTemplate}
                  >
                    Save as new template
                  </button>
                </>
              ) : (
                <button
                  className="brd-btn brd-btn--primary"
                  onClick={() => handleSaveTemplate(false)}
                  disabled={isSavingTemplate}
                >
                  {isSavingTemplate ? (
                    <span className="brd-spinner brd-spinner--sm"></span>
                  ) : (
                    'Save template'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}